import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Breadcrumb from '@/components/Breadcrumb';
import { BookOpen, ArrowRight } from 'lucide-react';
import { getArticle } from '@/data/resourceArticles';

export default function ResourceArticlePage() {
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="resource-error">
        Article not found. <Link to="/resources" className="text-sky-600 hover:underline ml-2">Back to resources</Link>
      </div>
    );
  }

  const canonical = `https://docsite.care/resources/${slug}`;
  const title = `${article.title} | DocSite`;
  const description = article.excerpt;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description,
    author: { '@type': 'Organization', name: 'DocSite' },
    publisher: {
      '@type': 'Organization', name: 'DocSite',
      logo: { '@type': 'ImageObject', url: 'https://docsite.care/logo512.png' },
    },
    mainEntityOfPage: canonical,
    keywords: (article.keywords || []).join(', '),
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={canonical} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <article className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <Breadcrumb items={[
          { label: 'DocSite', to: '/' },
          { label: 'Resources', to: '/resources' },
          { label: article.title.slice(0, 40) + (article.title.length > 40 ? '…' : '') },
        ]} />
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mt-6 mb-3 flex items-center gap-1.5">
          <BookOpen size={12} /> Guide for doctors
        </p>
        <h1 className="text-3xl md:text-4xl font-medium text-slate-900 tracking-tight leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="resource-h1">
          {article.title}
        </h1>
        <p className="text-base text-slate-600 mt-4 leading-relaxed">{article.excerpt}</p>

        <div className="mt-10 space-y-8" data-testid="resource-sections">
          {article.sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-xl font-medium text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.h}</h2>
              <p className="text-[15px] text-slate-700 leading-relaxed">{s.p}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-sky-200 bg-sky-50/40 p-6 text-center">
          <h3 className="text-lg font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to apply this?</h3>
          <p className="text-sm text-slate-600 mb-4">Build your doctor website on DocSite — free tier, no card required.</p>
          <Link to="/register?as=doctor&from=resource" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 transition-colors" data-testid="resource-cta">
            Start free <ArrowRight size={14} />
          </Link>
        </div>

        <div className="mt-10 pt-6 border-t border-slate-100">
          <Link to="/resources" className="text-sm text-sky-600 hover:underline">← All guides</Link>
        </div>
      </article>
    </div>
  );
}
